// frontend/src/pages/ClientPortalContent.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Image as ImageIcon,
  Video,
  Download,
  Search,
  CheckCircle,
  AlertCircle,
  Play,
  X,
  ZoomIn,
  FileText,
  Star,
  Building2,
  TrendingUp,
  Shield,
  Eye,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ChevronDown,
} from 'lucide-react';

const API_BASE_URL = 'https://patrol-visit.patroll-management.nissiindia.com';

// 🔗 Your files domain (adjust if different)
const FILE_BASE_URL = 'https://files.patroll-management.nissiindia.com';

// Clean + normalize media URLs so they work in production
const normalizeMediaUrl = (raw) => {
  if (!raw) return '';

  let url = String(raw).trim();
  if (
    !url ||
    url === '-' ||
    url.toLowerCase() === 'null' ||
    url.toLowerCase() === 'undefined'
  ) {
    return '';
  }

  // If relative path -> prefix with files domain
  if (url.startsWith('/')) {
    return `${FILE_BASE_URL.replace(/\/+$/, '')}${url}`;
  }

  // If protocol-relative, add https
  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  // Force https for files domain (avoid mixed content)
  if (url.startsWith('http://files.')) {
    return url.replace(/^http:/, 'https:');
  }

  return url;
};

// Get token (client or admin)
const getAuthToken = () =>
  localStorage.getItem('authToken') ||
  sessionStorage.getItem('authToken') ||
  localStorage.getItem('clientAuthToken') ||
  sessionStorage.getItem('clientAuthToken');

const ClientPortalContent = () => {
  const [client, setClient] = useState(null);
  const [properties, setProperties] = useState([]);
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState({
    totalVisits: 0,
    totalPhotos: 0,
    totalVideos: 0,
  });

  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);

  const [filterProperty, setFilterProperty] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDate, setFilterDate] = useState('all');

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Derived profile info
  const profileName = client?.fullName || 'Client';
  const profileEmail = client?.email || client?.username || '';

  const profileInitials = useMemo(() => {
    if (!profileName) return 'U';
    const parts = profileName.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }, [profileName]);

  // Logout handler
  const handleLogout = () => {
    try {
      // Clear all auth tokens you use
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('clientAuthToken');
      sessionStorage.removeItem('clientAuthToken');

      // Redirect to login page (adjust path if your login route is different)
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    if (!document.querySelector(`link[href="${link.href}"]`)) {
      document.head.appendChild(link);
    }
  }, []);

  // Fetch client portal data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        const token = getAuthToken();
        if (!token) {
          setError('Not logged in. Please login again.');
          setLoading(false);
          return;
        }

        // Admin can view specific client with ?clientId= in URL
        const params = new URLSearchParams(window.location.search);
        const clientId = params.get('clientId');
        const query = clientId ? `?clientId=${clientId}` : '';

        const res = await fetch(`${API_BASE_URL}/api/client-portal${query}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json().catch(() => ({}));
        console.log('client portal data:', data);

        if (!res.ok) {
          throw new Error(data.message || 'Failed to load data');
        }

        setClient(data.client || null);
        setProperties(data.properties || []);
        setVisits(data.visits || []);
        setStats(
          data.stats || {
            totalVisits: (data.visits || []).length,
            totalPhotos: 0,
            totalVideos: 0,
          }
        );
      } catch (err) {
        console.error('Client portal load error:', err);
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /* ---------- Download helpers ---------- */

  // Trigger browser download of a URL (single file)
  const triggerBrowserDownload = (url, filename) => {
    try {
      const link = document.createElement('a');
      link.href = url;

      if (filename) {
        link.download = filename;
      } else {
        link.target = '_blank';
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
      alert('Unable to start download. Please try again.');
    }
  };

  // Build safe filename for single media
  const buildFilename = (media, fallbackIndex, effectiveUrl) => {
    const cleanCaption = (media.caption || `media-${fallbackIndex}`)
      .toString()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9\-]/g, '')
      .toLowerCase();

    const urlPart = (effectiveUrl || media.url || '')
      .split('?')[0]
      .split('#')[0];
    const ext = urlPart.includes('.')
      ? urlPart.split('.').pop()
      : media.type === 'video'
      ? 'mp4'
      : 'jpg';

    return `${cleanCaption || 'media'}-${media.id || fallbackIndex}.${ext}`;
  };

  // Download a single media item (browser direct, no CORS issue)
  const handleDownloadMedia = (media) => {
    if (!media) return;
    const url = normalizeMediaUrl(media.url || media.thumbnail);
    if (!url) return;
    const filename = buildFilename(media, 1, url);
    triggerBrowserDownload(url, filename);
  };

  // Download all media for a visit as ZIP (via backend)
  const downloadAllMediaAsZip = async (visit) => {
    if (!visit || !visit.id) return;

    try {
      const token = getAuthToken();
      if (!token) {
        alert('Not logged in. Please login again.');
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/api/client-portal/visits/${visit.id}/download-zip`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('ZIP download failed:', res.status, text);
        alert('Unable to download media ZIP. Please try again.');
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visit-${visit.id}-media.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('ZIP download error:', err);
      alert('Unable to download media ZIP. Please try again.');
    }
  };

  /* ---------- Modal open/close ---------- */

  const openMediaModal = (media) => {
    setSelectedMedia(media);
    setShowMediaModal(true);
  };

  const closeMediaModal = () => {
    setShowMediaModal(false);
    setSelectedMedia(null);
  };

  /* ---------- Filtering + stats ---------- */

  const filteredVisits = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const filtered = visits.filter((visit) => {
      const matchesProperty =
        filterProperty === 'all' ||
        visit.propertyId === parseInt(filterProperty, 10);

      const matchesSearch =
        !searchQuery ||
        visit.propertyName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        visit.guard?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        visit.notes?.toLowerCase().includes(searchQuery.toLowerCase());

      let matchesDate = true;

      if (filterDate !== 'all' && visit.date) {
        const vDate = new Date(visit.date);
        const visitDay = new Date(
          vDate.getFullYear(),
          vDate.getMonth(),
          vDate.getDate()
        );

        if (filterDate === 'today') {
          matchesDate = visitDay.getTime() === startOfToday.getTime();
        } else if (filterDate === 'week') {
          const startOfWeek = new Date(startOfToday);
          startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(endOfWeek.getDate() + 7);
          matchesDate = visitDay >= startOfWeek && visitDay < endOfWeek;
        } else if (filterDate === 'month') {
          matchesDate =
            visitDay.getFullYear() === today.getFullYear() &&
            visitDay.getMonth() === today.getMonth();
        }
      }

      return matchesProperty && matchesSearch && matchesDate;
    });

    // Reset to page 1 when filters change
    setCurrentPage(1);

    return filtered;
  }, [visits, filterProperty, searchQuery, filterDate]);

  const totalVisits = stats.totalVisits ?? visits.length;
  const totalPhotos =
    stats.totalPhotos ??
    visits.reduce(
      (sum, v) =>
        sum +
        (v.mediaFiles
          ? v.mediaFiles.filter((m) => m.type === 'photo').length
          : 0),
      0
    );
  const totalVideos =
    stats.totalVideos ??
    visits.reduce(
      (sum, v) =>
        sum +
        (v.mediaFiles
          ? v.mediaFiles.filter((m) => m.type === 'video').length
          : 0),
      0
    );

  /* ---------- Pagination ---------- */

  const totalFilteredRecords = filteredVisits.length;
  const totalPages = Math.ceil(totalFilteredRecords / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredVisits.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  /* ---------- Loading + error ---------- */

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-[60vh]"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
          <p className="text-slate-600 font-medium">
            Loading your visit reports…
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 flex flex-col items-center textcenter gap-3"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <AlertCircle className="w-10 h-10 text-red-500" />
        <h3
          className="text-lg font-bold text-slate-800"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Unable to load data
        </h3>
        <p className="text-slate-600 text-sm">{error}</p>
      </div>
    );
  }

  /* ---------- MAIN RENDER ---------- */

  return (
    <div className="space-y-8" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-10 sm:p-12 shadow-2xl border border-slate-800">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        {/* Profile menu (top right) */}
        <div className="absolute top-6 right-6 z-10">
          <div className="relative">
            <button
              onClick={() => setShowProfileMenu((prev) => !prev)}
              className="flex items-center gap-3 px-3 py-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 backdrop-blur-md transition-all"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-semibold text-sm shadow-lg">
                {profileInitials}
              </div>
              <div className="hidden sm:flex flex-col text-left">
                <span
                  className="text-sm font-semibold text-white"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {profileName}
                </span>
                {profileEmail && (
                  <span
                    className="text-xs text-slate-200/80"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {profileEmail}
                  </span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-slate-100 hidden sm:block" />
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 mt-3 w-72 bg-slate-900/95 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
                <div className="p-4 flex gap-3 border-b border-slate-700/70">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                    {profileInitials}
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-sm font-semibold text-white"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {profileName}
                    </p>
                    {profileEmail && (
                      <p
                        className="text-xs text-slate-300 mt-0.5 break-all"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {profileEmail}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-red-400 hover:bg-red-500/10 transition-all"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="flex items-center gap-2">
                    <LogOut className="w-4 h-4" />
                    Log out
                  </span>
                  <span className="text-xs text-slate-400">End session</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-purple-400" />
            <span
              className="text-purple-300 font-semibold text-xs uppercase tracking-widest"
              style={{
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '0.15em',
              }}
            >
              Patrol Visit Client Portal by Eblackdog.com
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl font-bold text-white mb-3"
            style={{ fontFamily: 'Poppins, sans-serif', lineHeight: '1.2' }}
          >
            Property Patrol Visit Reports
          </h1>

          <p
            className="text-slate-300 text-base sm:text-lg max-w-3xl leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: '400' }}
          >
            {client
              ? `Welcome back, ${client.fullName}. View comprehensive patrol visit documentation and security reports here.`
              : 'Access detailed visit documentation with high-resolution photos, videos, and comprehensive security checklists from your patrol teams.'}
          </p>

          {/* Stats cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10">
            <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-emerald-500/20 p-3 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                </div>
                <Eye className="w-5 h-5 text-white/30" />
              </div>
              <p
                className="text-white/60 text-sm font-medium mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Total Visits
              </p>
              <p
                className="text-4xl font-bold text-white"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {totalVisits}
              </p>
              <p
                className="text-emerald-400 text-xs mt-2 font-medium"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                All time
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-blue-500/20 p-3 rounded-xl">
                  <ImageIcon className="w-6 h-6 text-blue-400" />
                </div>
                <Eye className="w-5 h-5 text-white/30" />
              </div>
              <p
                className="text-white/60 text-sm font-medium mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Photos Captured
              </p>
              <p
                className="text-4xl font-bold text-white"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {totalPhotos}
              </p>
              <p
                className="text-blue-400 text-xs mt-2 font-medium"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                High resolution
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-pink-500/20 p-3 rounded-xl">
                  <Video className="w-6 h-6 text-pink-400" />
                </div>
                <Eye className="w-5 h-5 text-white/30" />
              </div>
              <p
                className="text-white/60 text-sm font-medium mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Video Reports
              </p>
              <p
                className="text-4xl font-bold text-white"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {totalVideos}
              </p>
              <p
                className="text-pink-400 text-xs mt-2 font-medium"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Recorded footage
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label
              className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by property, guard, or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="min-w-[220px]">
              <label
                className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Property
              </label>
              <select
                value={filterProperty}
                onChange={(e) => setFilterProperty(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all text-sm font-medium"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="all">All Properties</option>
                {properties.map((prop) => (
                  <option key={prop.id} value={prop.id}>
                    {prop.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-w-[180px]">
              <label
                className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Date Range
              </label>
              <select
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all text-sm font-medium"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Visits list */}
      <div className="space-y-6">
        {currentRecords.map((visit) => (
          <div
            key={visit.id}
            className="group bg-white rounded-2xl shadow-sm hover:shadow-xl border border-slate-200 overflow-hidden transition-all duration-300"
          >
            {/* Visit header */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-8 border-b border-slate-200">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white shadow-lg flex-shrink-0">
                      <Building2 className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <h2
                        className="text-2xl font-bold text-slate-900 mb-2"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {visit.propertyName}
                      </h2>
                      <p
                        className="text-sm text-slate-600 flex items-center gap-2"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span>{visit.propertyAddress}</span>
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-white px-4 py-3 rounded-xl border border-slate-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-purple-600" />
                        <span
                          className="text-xs text-slate-500 font-medium"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Date
                        </span>
                      </div>
                      <p
                        className="font-semibold text-slate-900 text-sm"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {visit.date}
                      </p>
                    </div>

                    <div className="bg-white px-4 py-3 rounded-xl border border-slate-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span
                          className="text-xs text-slate-500 font-medium"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Time
                        </span>
                      </div>
                      <p
                        className="font-semibold text-slate-900 text-sm"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {visit.time}
                      </p>
                    </div>

                    <div className="bg-white px-4 py-3 rounded-xl border border-slate-200">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-emerald-600" />
                        <span
                          className="text-xs text-slate-500 font-medium"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Guard
                        </span>
                      </div>
                      <p
                        className="font-semibold text-slate-900 text-sm"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {visit.guard}
                      </p>
                    </div>

                    <div className="bg-white px-4 py-3 rounded-xl border border-slate-200">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-orange-600" />
                        <span
                          className="text-xs text-slate-500 font-medium"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Duration
                        </span>
                      </div>
                      <p
                        className="font-semibold text-slate-900 text-sm"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {visit.duration || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-start lg:items-end gap-3">
                  <span
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-50 text-emerald-700 rounded-xl font-semibold text-sm border border-emerald-200"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {visit.status || 'Completed'}
                  </span>
                </div>
              </div>
            </div>

            {/* Visit body */}
            <div className="p-8">
              {/* Notes */}
              <div className="mb-8">
                <h3
                  className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wider flex items-center gap-2"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  <FileText className="w-5 h-5 text-purple-600" />
                  Visit Notes
                </h3>
                <div className="bg-slate-50 border-l-4 border-purple-600 p-5 rounded-r-xl">
                  <p
                    className="text-slate-700 leading-relaxed"
                    style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}
                  >
                    {visit.notes || 'No additional notes provided for this visit.'}
                  </p>
                </div>
              </div>

              {/* Checklist */}
              {visit.checklist && visit.checklist.length > 0 && (
                <div className="mb-8">
                  <h3
                    className="text-sm font-bold text-slate-900 mb-4 uppercase tracking-wider flex items-center gap-2"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    Security Checklist
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {visit.checklist.map((item, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                          item.checked
                            ? 'bg-emerald-50 border-emerald-300'
                            : 'bg-amber-50 border-amber-300'
                        }`}
                      >
                        {item.checked ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        )}
                        <span
                          className={`text-sm font-medium ${
                            item.checked ? 'text-emerald-900' : 'text-amber-900'
                          }`}
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {item.item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Media gallery */}
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h3
                    className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    <ImageIcon className="w-5 h-5 text-blue-600" />
                    Media Gallery
                    <span
                      className="ml-2 text-xs font-semibold px-3 py-1 bg-slate-100 text-slate-600 rounded-full"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {visit.mediaFiles?.length || 0} files
                    </span>
                  </h3>

                  {visit.mediaFiles && visit.mediaFiles.length > 0 && (
                    <button
                      onClick={() => downloadAllMediaAsZip(visit)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all hover:scale-105"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <Download className="w-4 h-4" />
                      Download All (ZIP)
                    </button>
                  )}
                </div>

                {visit.mediaFiles && visit.mediaFiles.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {visit.mediaFiles.map((media) => {
                      const thumbUrl = normalizeMediaUrl(
                        media.thumbnail || media.url
                      );
                      return (
                        <div
                          key={media.id}
                          className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow-md hover:shadow-2xl transition-all hover:scale-105 border border-slate-200"
                          onClick={() => openMediaModal(media)}
                        >
                          <img
                            src={thumbUrl}
                            alt={media.caption}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />

                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                              <p
                                className="text-white text-xs font-medium mb-1 line-clamp-2"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {media.caption}
                              </p>
                              <p
                                className="text-slate-300 text-xs"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {media.timestamp}
                              </p>
                            </div>
                          </div>

                          <div className="absolute top-2 right-2">
                            {media.type === 'video' ? (
                              <div className="bg-red-500 text-white p-2 rounded-lg shadow-lg">
                                <Play className="w-3.5 h-3.5" />
                              </div>
                            ) : (
                              <div className="bg-blue-500 text-white p-2 rounded-lg shadow-lg">
                                <ImageIcon className="w-3.5 h-3.5" />
                              </div>
                            )}
                          </div>

                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-white/95 backdrop-blur-sm p-3 rounded-full shadow-xl">
                              <ZoomIn className="w-5 h-5 text-purple-600" />
                            </div>
                          </div>

                          {media.type === 'video' && media.duration && (
                            <div
                              className="absolute top-2 left-2 bg-black/90 text-white px-2.5 py-1 rounded-lg text-xs font-bold"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              {media.duration}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                    <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p
                      className="text-sm text-slate-500 font-medium"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      No media files uploaded for this visit
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalFilteredRecords > 0 && totalPages > 1 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div
              className="text-sm text-slate-600"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Showing{' '}
              <span className="font-semibold text-slate-900">
                {indexOfFirstRecord + 1}
              </span>{' '}
              to{' '}
              <span className="font-semibold text-slate-900">
                {Math.min(indexOfLastRecord, totalFilteredRecords)}
              </span>{' '}
              of{' '}
              <span className="font-semibold text-slate-900">
                {totalFilteredRecords}
              </span>{' '}
              visits
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  currentPage === 1
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-white border-2 border-slate-300 text-slate-700 hover:bg-purple-50 hover:border-purple-400 hover:text-purple-700'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Previous</span>
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers().map((pageNumber, index) => (
                  <React.Fragment key={index}>
                    {pageNumber === '...' ? (
                      <span
                        className="px-3 py-2 text-slate-400 font-bold"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        onClick={() => goToPage(pageNumber)}
                        className={`min-w-[44px] h-11 rounded-xl font-bold text-sm transition-all ${
                          currentPage === pageNumber
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg scale-105 border-2 border-purple-400'
                            : 'bg-white border-2 border-slate-300 text-slate-700 hover:bg-purple-50 hover:border-purple-400 hover:text-purple-700'
                        }`}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {pageNumber}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div
                className="sm:hidden px-4 py-2.5 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl font-bold text-sm text-purple-700"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {currentPage} / {totalPages}
              </div>

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  currentPage === totalPages
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-white border-2 border-slate-300 text-slate-700 hover:bg-purple-50 hover:border-purple-400 hover:text-purple-700'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div
              className="flex items-center gap-2 text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <label className="text-slate-600 font-medium">Show:</label>
              <select
                value={recordsPerPage}
                onChange={() => {
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-white border border-slate-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none transition-all font-medium"
                disabled
              >
                <option value={10}>10</option>
              </select>
              <span className="text-slate-600">per page</span>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {filteredVisits.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-16 text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-purple-50 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-white shadow-lg">
            <FileText className="w-12 h-12 text-purple-600" />
          </div>
          <h3
            className="text-2xl font-bold text-slate-900 mb-3"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            No Visits Found
          </h3>
          <p
            className="text-slate-600 max-w-md mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            We couldn't find any visits matching your current filters. Try
            adjusting your search criteria or date range.
          </p>
        </div>
      )}

      {/* Media Modal */}
      {showMediaModal && selectedMedia && (
        <div className="fixed inset-0 bg-black/97 backdrop-blur-md z-50 flex items-center justify-center p-6 animate-fadeIn">
          <div className="max-w-7xl w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-3.5 rounded-xl shadow-lg">
                  {selectedMedia.type === 'video' ? (
                    <Video className="w-6 h-6 text-white" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h3
                    className="text-white text-xl font-bold mb-1"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {selectedMedia.caption}
                  </h3>
                  <p
                    className="text-slate-400 text-sm"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {selectedMedia.timestamp}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Download single media */}
                <button
                  onClick={() => handleDownloadMedia(selectedMedia)}
                  className="p-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl transition-all border border-white/10"
                  title="Download"
                >
                  <Download className="w-5 h-5 text-black" />
                </button>

                {/* Close (X) */}
                <button
                  onClick={closeMediaModal}
                  className="p-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl transition-all border border-white/10"
                  title="Close"
                >
                  <X className="w-5 h-5 text-black" />
                </button>
              </div>
            </div>

            {/* Media Content */}
            <div className="bg-slate-900/50 rounded-2xl overflow-hidden shadow-2xl border border-slate-800">
              {selectedMedia.type === 'video' ? (
                <video
                  src={normalizeMediaUrl(
                    selectedMedia.url || selectedMedia.thumbnail
                  )}
                  controls
                  autoPlay
                  className="w-full max-h-[80vh]"
                />
              ) : (
                <img
                  src={normalizeMediaUrl(
                    selectedMedia.url || selectedMedia.thumbnail
                  )}
                  alt={selectedMedia.caption}
                  className="w-full max-h-[80vh] object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ClientPortalContent;
